import { Link } from "wouter";
import { Lock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PLAN_META, type FeatureKey } from "@workspace/plans";
import { useFeature } from "@/lib/useFeature";
import { useDemoMode } from "@/components/DemoBanner";

interface LockedPreviewProps {
  feature: FeatureKey;
  /** When the feature is enabled, render this. */
  children: React.ReactNode;
  /** Optional alternate title; defaults to the feature label. */
  title?: string;
  /** Optional alternate body copy. */
  description?: string;
  /** Render style: card overlay (default) or inline pill. */
  variant?: "card" | "inline";
}

/**
 * Renders `children` when the org's plan unlocks the feature; otherwise
 * shows a locked upsell overlay that links to /settings.
 */
export function LockedPreview({
  feature,
  children,
  title,
  description,
  variant = "card",
}: LockedPreviewProps) {
  const f = useFeature(feature);
  const isDemo = useDemoMode();
  // Demo orgs showcase the full product and never see an upgrade CTA — the
  // account team manages billing — so premium content renders unlocked.
  if (f.enabled || isDemo) return <>{children}</>;

  const requiredMeta = PLAN_META[f.requiredPlan];

  if (variant === "inline") {
    return (
      <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800">
        <Lock className="h-3 w-3" />
        <span>{title ?? f.label}</span>
        <Link href="/settings" className="underline hover:no-underline">
          Upgrade to {requiredMeta.name}
        </Link>
      </div>
    );
  }

  return (
    <div className="relative rounded-xl border border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-white overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none select-none opacity-40 blur-[2px] grayscale max-h-64 overflow-hidden"
      >
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[1px]">
        <div className="text-center max-w-sm p-6">
          <div className="mx-auto w-10 h-10 rounded-full bg-[#0B1834] text-white flex items-center justify-center mb-3">
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">
            {requiredMeta.name} feature
          </div>
          <h3 className="font-semibold text-gray-900">{title ?? f.label}</h3>
          <p className="mt-1 text-sm text-gray-500 leading-relaxed">
            {description ?? f.description}
          </p>
          <Button asChild size="sm" className="mt-4 bg-[#0B1834] hover:bg-[#0A152E] text-white">
            <Link href="/settings">Upgrade to {requiredMeta.name}</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
